import React, { useCallback } from 'react';
import axios from 'axios';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { messages } from '../../../_shared/i18n/messages';

const axiosClient = axios.create({
	withCredentials: true,
});

const propTypes = {

	/**
	 * The input id. If none is provided, the input name is used.
	 */
	id: PropTypes.string,

	/**
	 * The input name.
	 */
	name: PropTypes.string.isRequired,

	/**
	 * The input value.
	 */
	value: PropTypes.string,

	/**
	 * The input placeholder text.
	 */
	placeholder: PropTypes.string,

	/**
	 * True, if editing the input value.
	 */
	isEditing: PropTypes.bool,

	/**
	 * True, if the input value is invalid.
	 */
	isInvalid: PropTypes.bool,

	/**
	 * Callback function to handle changes to the input value.
	 */
	onChange: PropTypes.func.isRequired,

	/**
	 * True, if the input value is disabled.
	 */
	disabled: PropTypes.bool,

	/**
	 * Callback function to handle changes to the metadata for the input value.
	 */
	onChangeMeta: PropTypes.func.isRequired,

};

const LocationInput = (props) => {

	const {
		id,
		name,
		value,
		placeholder,
		isEditing,
		isInvalid,
		disabled,
		onChange,
		onChangeMeta
	} = props;

	// Listen for changes to the location input value and fetch metadata every 500ms
	const handleMetadataChange = useCallback(debounce((location) => {

		if (!location) {
			onChangeMeta(null);
			return;
		}

		// NOTE: API responds with 400 error for a single character input
		if (location.length > 1) {

			axiosClient.get('/api/v2/geocode', {
				params: {
					location,
				}
			}).then((response) => {

				const data = response?.data?.results[0];

				if (data) {

					onChangeMeta({
						location,
						formatted: data.formatted,
						lat: data.geometry?.lat,
						lng: data.geometry?.lng,
					});

				}

			}).catch((error) => {
				console.error(error);
			});

		}

	}, 500), []);

	if (!isEditing) {

		return (
			<div>
				{value ||
					<span className="tc-material-input-display-empty-value">
						<FormattedMessage {...messages.TC_COMPONENTS_FORM_INPUT_TEXT_EMPTY_VALUE} />
					</span>
				}
			</div>
		);

	}

	const handleChange = (event) => {

		onChange(event.target.value);

		handleMetadataChange(event.target.value);

	};

	return (
		<input
			id={id || name}
			className={`tc-material-input ${isInvalid ? 'tc-material-input-invalid' : ''}`}
			type="text"
			name={name}
			value={value}
			placeholder={placeholder}
			disabled={disabled}
			onChange={handleChange} />
	);

};

LocationInput.propTypes = propTypes;

export default LocationInput;
